import FileCopy from '@mui/icons-material/FileCopy'
import {
  Box,
  Card,
  CardContent,
  Icon,
  IconButton,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import React from 'react'

import { SchemaCustomfield } from '@/__generated/es-api'
import CreateButton from '@/app/component/atom/create-button'
import DeleteDialog from '@/app/component/atom/delete-dialog'
import ExternalLink from '@/app/component/atom/external-link'
import Loader from '@/app/component/atom/loader'
import SupportedIcon from '@/app/component/atom/supported-icon'
import TabHeader from '@/app/component/atom/tab-header'
import CreateCustomFieldsDialog from '@/app/module/custom-fields/component/create-custom-fields-dialog'
import CustomFieldActionMenu from '@/app/module/custom-fields/component/custom-field-action-menu'
import UpdateCustomFieldDialog from '@/app/module/custom-fields/component/update-custom-field-dialog'
import { useDeleteCustomField } from '@/app/module/custom-fields/hooks/use-delete-custom-field'
import { useGetCustomFields } from '@/app/module/custom-fields/hooks/use-get-custom-fields'
import { createNotification } from '@/app/module/notifications/store/actions'
import { useAppDispatch } from '@/app/store/redux-hooks'

type Props = Record<string, never>

const CustomFields: React.FC<Props> = () => {
  const { data, isFetching, refetch } = useGetCustomFields()
  const [deleteCustomFieldById, { isLoading: isDeleting }] = useDeleteCustomField()

  const [createDialogOpen, setCreateDialogOpen] = React.useState(false)
  const [updateDialogOpen, setUpdateDialogOpen] = React.useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [selectedCustomField, setSelectedCustomField] = React.useState<SchemaCustomfield | undefined>(undefined)
  const [toBeDeleted, setTobeDeleted] = React.useState<SchemaCustomfield | undefined>(undefined)

  const customFields = React.useMemo(() => [...(data?.allCustomfields || [])], [data])

  const handleOpenCreateDialog = () => {
    setCreateDialogOpen(true)
  }

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false)
  }

  const handleOpenUpdateDialog = (customField: SchemaCustomfield) => {
    setSelectedCustomField(customField)
    setUpdateDialogOpen(true)
  }

  const handleCloseUpdateDialog = () => {
    setUpdateDialogOpen(false)
    setSelectedCustomField(undefined)
  }

  const handleOpenDeleteDialog = (customField: SchemaCustomfield) => {
    setTobeDeleted(customField)
    setDeleteDialogOpen(true)
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setTobeDeleted(undefined)
  }

  const handleConfirmDelete = async () => {
    if (!toBeDeleted) {
      return
    }

    const result = await deleteCustomFieldById(toBeDeleted.id.toString(), toBeDeleted.name)
    if (result) {
      handleCloseDeleteDialog()
    }
  }

  const dispatch = useAppDispatch()

  const handleCopyId = (id: number) => {
    navigator.clipboard.writeText(id.toString()).then(() => {
      dispatch(
        createNotification({
          'copy-custom-field-id': {
            type: 'success',
            message: 'Custom Field ID copied!',
          },
        }),
      )
    })
  }

  return (
    <Container>
      <TabHeader
        title="Custom Fields"
        action={
          <div style={{ display: 'flex', gap: '8px' }}>
            <CreateButton onClick={handleOpenCreateDialog}>New custom fields</CreateButton>
            <IconButton onClick={refetch} title="Reload custom fields">
              <Icon>refresh</Icon>
            </IconButton>
          </div>
        }
      />

      <ContentContainer>
        <Typography variant="body2" style={{ marginBottom: '16px' }}>
          List of all custom fields in your organization with their IDs and names. Use these IDs when working with the
          API or when configuring campaigns. Read more about custom fields in{' '}
          <ExternalLink
            href="https://www.engagespark.com/support/what-are-custom-fields-how-to-add-them/"
            target="_blank"
            rel="noopener noreferrer"
          >
            this support article.
          </ExternalLink>
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeadCell width="100px">
                  <Typography variant="subtitle2" fontWeight="bold">
                    ID
                  </Typography>
                </TableHeadCell>
                <TableHeadCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Name
                  </Typography>
                </TableHeadCell>
                <TableHeadCell align="center">
                  <Typography variant="subtitle2" fontWeight="bold">
                    Access
                  </Typography>
                </TableHeadCell>
                <TableHeadCell width="120px" align="center">
                  <Typography variant="subtitle2" fontWeight="bold">
                    Actions
                  </Typography>
                </TableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customFields.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" style={{ padding: '48px' }}>
                    <Typography variant="body1" color="textSecondary">
                      No custom fields found. Create custom fields by clicking "Add Custom Fields" above.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                customFields.map((field) => (
                  <TableRow key={field.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap="8px">
                        <Typography variant="body2">{field.id}</Typography>
                        <Tooltip title="Copy ID" arrow>
                          <IconButton onClick={() => handleCopyId(field.id)}>
                            <FileCopy color="action" fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{field.name}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <SupportedIcon supported={field.access} />
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          '& > div': {
                            marginLeft: 'auto',
                            marginRight: 'auto',
                          },
                        }}
                      >
                        <CustomFieldActionMenu
                          customField={field}
                          onEdit={handleOpenUpdateDialog}
                          onDelete={handleOpenDeleteDialog}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {customFields.length > 0 && (
          <Typography variant="caption" color="textSecondary" style={{ marginTop: '16px', display: 'block' }}>
            Total: {customFields.length} custom field{customFields.length !== 1 ? 's' : ''}
          </Typography>
        )}
      </ContentContainer>
      {isFetching && <Loader />}

      <CreateCustomFieldsDialog open={createDialogOpen} onClose={handleCloseCreateDialog} />

      {selectedCustomField && (
        <UpdateCustomFieldDialog
          customField={selectedCustomField}
          open={updateDialogOpen}
          onClose={handleCloseUpdateDialog}
        />
      )}

      {toBeDeleted && (
        <DeleteDialog
          deleteButtonLoading={isDeleting}
          isOpen={deleteDialogOpen}
          text={`Are you sure you want to delete the custom field "${toBeDeleted.name}"?`}
          onClose={handleCloseDeleteDialog}
          onConfirm={handleConfirmDelete}
        />
      )}
    </Container>
  )
}

const Container = styled(Card)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  maxHeight: '100%',
})

const ContentContainer = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100% - 64px)', // Adjust height to account for header
})

const TableContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,
  overflow: 'auto',

  '& table': {
    borderCollapse: 'separate',
    borderSpacing: 0,
    borderTop: 'none',
  },
})

const TableHeadCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  position: 'sticky',
  top: 0,
  zIndex: 1,
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
}))

export default CustomFields
